//
// Copyright (c) 2011-2021 by Curalate, Inc.
//

import { createSelector } from 'reselect';

import { IScheduledInstagramPost } from '@apis/universal-posts/scheduled-posts/universalScheduledPostsApiModel';
import { IFeedPreviewItem } from '@instagram-account/redux/schedule/feed-preview/instagramScheduleFeedPreviewModel';
import { shouldShowInFeedPreview } from '@instagram-account/redux/schedule/feed-preview/instagramScheduleFeedPreviewUtils';
import {
  getInstagramStandardPostDetails,
  getMediaOverlayIconType,
} from '@instagram-account/redux/schedule/instagramScheduleUtils';
import { getOrderedPosts } from '@instagram-account/redux/schedule/selectors/getOrderedPosts';
import { ICuralateStore } from '@models/AppState';
import {
  IInstagramPost,
  getMediaOverlayIconType as getLiveMediaOverlayIconType,
} from '@models/IInstagramMediaItem';
import { hasFullMediaAndNetworkItem } from '@models/IMlsMediaItem';
import TimeZoneId from '@models/TimeZoneId';
import { getUserTimeZone } from '@redux/selectors/getUserTimeZone';
import { isDefined } from '@utils/filterPredicates';
import { getMediaMedsSizedImageUrl } from '@utils/meds';
import { Normalized } from '@utils/normalize';
import { truncateToMultiple } from '@utils/truncateToMultiple';

export const getScheduledFeedPreviewItemsImpl = (
  posts: IScheduledInstagramPost[],
  tz: TimeZoneId,
): IFeedPreviewItem[] => {
  return (
    posts
      // We can't display sufficient UI without a networkItem, so don't
      // bother including items that don't have one in the feed preview.
      .filter(hasFullMediaAndNetworkItem)
      .filter((post) => shouldShowInFeedPreview(post, tz))
      .reduce<IFeedPreviewItem[]>((posts, post) => {
        const { previewMediaItem } = getInstagramStandardPostDetails(post);
        if (isDefined(post.config.cuids[0]) && isDefined(previewMediaItem)) {
          posts.push({
            cuid: post.config.cuids[0],
            imageUrl: getMediaMedsSizedImageUrl(previewMediaItem, 'm'),
            mediaType: getMediaOverlayIconType(post),
            postId: post.id,
            scheduledTime: post.scheduledTime,
          });
        }

        return posts;
      }, [])
  );
};

const getScheduledFeedPreviewItems = createSelector(
  getOrderedPosts,
  getUserTimeZone,
  getScheduledFeedPreviewItemsImpl,
);

const getOrderedLivePosts = createSelector(
  (state: ICuralateStore) => state.instagram.schedule.feedPreview.livePosts,
  (normalizedLivePosts) => Normalized.getOrdered(normalizedLivePosts),
);

export const getLivePostFeedPreviewItemsImpl = (posts: IInstagramPost[]) => {
  return posts.map((post) => ({
    cuid: post.cuid,
    imageUrl: post.media.image?.url ?? '',
    mediaType: getLiveMediaOverlayIconType(post),
    postedTime: post.postedTime,
  }));
};

const getLivePostFeedPreviewItems = createSelector(
  getOrderedLivePosts,
  getLivePostFeedPreviewItemsImpl,
);

export const getFeedPreviewItemsImpl = (
  scheduledItems: IFeedPreviewItem[],
  liveItems: IFeedPreviewItem[],
) => {
  const reversedScheduledItems = [...scheduledItems].reverse();

  // Filter out any past due posts that were scheduled to be posted before
  // the last live post because the user would not be able to post this
  // item in that order
  const mostRecentLiveItem = liveItems[0];
  const mostRecentLiveItemPostedTime = mostRecentLiveItem?.postedTime ?? 0;
  const reversedScheduledItemsFiltered = reversedScheduledItems.filter(
    (scheduledItem) => {
      const scheduledTime = scheduledItem.scheduledTime ?? 0;
      return +scheduledTime > mostRecentLiveItemPostedTime;
    },
  );

  // We round to multiples of 3 to show a neat feed preview grid.
  return truncateToMultiple(
    [...reversedScheduledItemsFiltered, ...liveItems],
    3,
  );
};

export const getFeedPreviewItems = createSelector(
  getScheduledFeedPreviewItems,
  getLivePostFeedPreviewItems,
  getFeedPreviewItemsImpl,
);
